import React, { useState, useEffect } from 'react';
import { Card, Button, Spin, Badge, Tag, message, Select, Divider, Alert } from 'antd';
import { HolderOutlined, ReloadOutlined, SaveOutlined, InfoCircleOutlined } from '@ant-design/icons';
import superuserService from '@/services/superadminService';
import { useParams } from 'react-router-dom';

const { Option } = Select;

const ModuleOrderManager = () => {
  const [eventModules, setEventModules] = useState({});
  const [allUniqueModules, setAllUniqueModules] = useState([]);
  const [originalModuleOrder, setOriginalModuleOrder] = useState([]);
  const [moduleOrder, setModuleOrder] = useState([]);
  const [savedModuleOrder, setSavedModuleOrder] = useState([]); // Store the excel_order from DB
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [moduleStats, setModuleStats] = useState({});
  const { id: moduleGroupId } = useParams();

  useEffect(() => {
    fetchModuleGroupData();
  }, [moduleGroupId]);

  useEffect(() => {
    if (Object.keys(eventModules).length > 0) {
      extractUniqueModules();
    }
  }, [eventModules]);

  const fetchModuleGroupData = async () => {
    try {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));

      const res = await superuserService.getsinglemodulegroup(moduleGroupId, "DAILY_REPORT");
      const idData = res.data?.event_modules || {};
      const excelOrder = res.data?.excel_order || []; // Get saved excel_order

      setEventModules(idData);
      setSavedModuleOrder(excelOrder); // Store the saved order
    } catch (error) {
      message.error('Failed to fetch module group data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const extractUniqueModules = () => {
    const moduleMap = new Map();
    const stats = {};

    // Iterate through all event types
    Object.entries(eventModules).forEach(([eventType, modules]) => {
      modules.forEach(module => {
        const moduleName = module.name;

        // Track module statistics
        if (!stats[moduleName]) {
          stats[moduleName] = {
            count: 0,
            eventTypes: []
          };
        }
        stats[moduleName].count += 1;
        stats[moduleName].eventTypes.push(eventType);

        // Store unique modules (first occurrence determines position)
        if (!moduleMap.has(moduleName)) {
          moduleMap.set(moduleName, {
            name: moduleName,
            firstSeenIn: eventType,
            fields: module.fields
          });
        }
      });
    });

    // Convert map to array maintaining insertion order
    const uniqueModules = Array.from(moduleMap.values());
    let moduleNames = uniqueModules.map(m => m.name);

    // Apply saved excel_order if it exists and contains all modules
    if (savedModuleOrder.length > 0) {
      // Filter saved order to only include modules that exist in current data
      const validSavedOrder = savedModuleOrder.filter(name => 
        moduleNames.includes(name)
      );
      
      // Add any new modules that weren't in saved order
      const newModules = moduleNames.filter(name => 
        !validSavedOrder.includes(name)
      );

      moduleNames = [...validSavedOrder, ...newModules];
    }

    setAllUniqueModules(uniqueModules);
    setModuleOrder(moduleNames);
    setOriginalModuleOrder([...moduleNames]);
    setModuleStats(stats);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const items = Array.from(moduleOrder);
    const draggedItem = items[draggedIndex];

    items.splice(draggedIndex, 1);
    items.splice(index, 0, draggedItem);

    setModuleOrder(items);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);

      // Create reordered event modules based on new module order
      const reorderedEventModules = {};

      Object.entries(eventModules).forEach(([eventType, modules]) => {
        // Sort modules based on the new moduleOrder
        const sortedModules = [...modules].sort((a, b) => {
          const indexA = moduleOrder.indexOf(a.name);
          const indexB = moduleOrder.indexOf(b.name);
          return indexA - indexB;
        });

        reorderedEventModules[eventType] = sortedModules;
      });

      // Prepare payload for your API
      const payload = {
        mapper_id: moduleGroupId,
        excel_order: moduleOrder
      };

      await superuserService.updatemodulegroupbyname(payload);

      setEventModules(reorderedEventModules);
      setOriginalModuleOrder([...moduleOrder]);
      setSavedModuleOrder([...moduleOrder]); // Update saved order
      setHasChanges(false);
      message.success('Module order updated successfully!');
    } catch (error) {
      message.error('Failed to update module order');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setModuleOrder([...originalModuleOrder]);
    setHasChanges(false);
    message.info('Changes reset to last saved order');
  };

  const getModuleColor = (moduleName) => {
    const colors = [
      '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
      '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
    ];
    const index = originalModuleOrder.indexOf(moduleName);
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spin size="large" tip="Loading module data..." />
      </div>
    );
  }

  return (
    <Card
      className="shadow-sm"
      title={
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <h6 className="mb-0">Module Order Configuration</h6>
            <Badge
              count={moduleOrder.length}
              style={{ backgroundColor: '#52c41a' }}
            />
            {savedModuleOrder.length > 0 && (
              <Tag color="blue">Saved Order Applied</Tag>
            )}
          </div>
          <div className="d-flex gap-2 mt-2 mt-md-0">
             
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              disabled={!hasChanges || saving}
              loading={saving}
            >
              Save Order
            </Button>
          </div>
        </div>
      }
    >
      {moduleOrder.length > 0 ? (
        <>
          <div
            className="p-3 rounded"
            style={{
              minHeight: '200px',
              backgroundColor: '#fafafa',
              border: '2px dashed #d9d9d9',
              transition: 'all 0.3s'
            }}
          >
            <div className="d-flex flex-wrap gap-3">
              {moduleOrder.map((moduleName, index) => {
                const stats = moduleStats[moduleName];
                return (
                  <div
                    key={moduleName}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <div
                      style={{
                        cursor: 'move',
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        border: draggedIndex === index ? `2px solid ${getModuleColor(moduleName)}` : '1px solid #d9d9d9',
                        borderRadius: '8px',
                        boxShadow: draggedIndex === index ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 4px rgba(0,0,0,0.05)',
                        transform: draggedIndex === index ? 'scale(1.05) rotate(2deg)' : 'scale(1)',
                        transition: 'all 0.2s',
                        minWidth: '160px'
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <HolderOutlined style={{ color: '#8c8c8c', fontSize: '16px' }} />
                        <span className="fw-bold" style={{ fontSize: '14px' }}>
                          {moduleName}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                        Position: {index + 1}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-3 pt-3 border-top text-center">
            <small className="text-muted">
              <InfoCircleOutlined className="me-2" />
              Drag and drop modules to reorder. Changes will apply to all event types that contain these modules.
            </small>
          </div>
        </>
      ) : (
        <Alert
          message="No Modules Found"
          description="No modules are available in the current module group."
          type="info"
          showIcon
        />
      )}
    </Card>
  );
};

export default ModuleOrderManager;